import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { NormalizedTemplate, TemplateLoadError } from '../types/template';
import { loadTemplate } from '../services/template-loader';
import { ERROR_CODES } from '../constants/default';

/**
 * Reactive Controller that encapsulates template loading with
 * AbortSignal support, configurable timeout, and clean lifecycle management.
 *
 * Usage:
 *   this._templateLoader = new TemplateLoaderController(this);
 *   const outcome = await this._templateLoader.load(url, timeoutMs);
 */
export class TemplateLoaderController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _abortController: AbortController | null = null;
  private _timeoutId: ReturnType<typeof setTimeout> | null = null;
  private _timedOut = false;

  isRunning = false;
  template: NormalizedTemplate | null = null;
  error: TemplateLoadError | null = null;

  async load(url: string, timeoutMs: number): Promise<'completed' | 'aborted'> {
    // Clear stale state and reset _timedOut flag
    this.template = null;
    this.error = null;
    this._timedOut = false;

    // Abort previous in-flight fetch and its timer if running
    if (this._abortController) {
      this._clearTimeout();
      this._abortController.abort();
    }

    // Create new AbortController, set isRunning, notify host
    this._abortController = new AbortController();
    this.isRunning = true;
    this.host.requestUpdate();

    // Start timeout — sets _timedOut flag then aborts the fetch
    this._timeoutId = setTimeout(() => {
      this._timedOut = true;
      this._abortController?.abort();
    }, timeoutMs);

    // Call loadTemplate with the signal
    try {
      const result = await loadTemplate(url, this._abortController.signal);

      // Clear timeout on completion
      this._clearTimeout();

      if (result.ok) {
        // Success path
        this.template = result.template;
      } else {
        // Failure path (service returned ok: false)
        this.error = { title: result.title, errors: result.errors };
      }

      this.isRunning = false;
      this.host.requestUpdate();
      return 'completed';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (this._timedOut) {
          // Timeout-caused abort: set timeout error (timer already fired, no need to clear)
          this.error = {
            title: 'Template took too long to load',
            errors: [
              {
                path: 'template-url',
                message: `Template load exceeded ${timeoutMs}ms`,
                code: ERROR_CODES.TEMPLATE_TIMEOUT,
              },
            ],
          };
          this.isRunning = false;
          this.host.requestUpdate();
          return 'completed';
        }

        // Superseded or disconnected abort: resolve silently (don't touch current timer)
        return 'aborted';
      }

      // Unexpected error — we own the timer, clear it
      this._clearTimeout();

      // Treat as failure
      this.error = {
        title: 'Unable to Load Template',
        errors: [
          {
            path: 'template-url',
            message: (err as Error).message ?? 'Unknown error',
            code: ERROR_CODES.TEMPLATE_FETCH_ERROR,
          },
        ],
      };
      this.isRunning = false;
      this.host.requestUpdate();
      return 'completed';
    }
  }

  reset(): void {
    this.template = null;
    this.error = null;
    this.isRunning = false;
    this._clearTimeout();
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this.host.requestUpdate();
  }

  private _clearTimeout(): void {
    if (this._timeoutId !== null) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    this.host.addController(this);
  }

  hostDisconnected(): void {
    this.isRunning = false;
    this._clearTimeout();
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }
}
